/**
 * ChatInput store — leaf module (Perf 3.1.b Step 4). Composer input state,
 * parameters, send orchestration. Sibling stores via getStores().
 */
import { create } from 'zustand';
import { chatsApi } from '@/lib/api/chats';
import { isAbortedError } from '@/lib/api/client';
import {
  clearAllComposerDrafts,
  clearComposerDraft,
  readComposerDraft,
  scheduleSaveComposerDraft,
} from '@/lib/composerDraft';

const getStores = () => require('@/store');

// Temporary chats must never survive a restart — see the `partialize` comment
// in store/chat.js. sessionStorage is restored by Chromium's crash/session
// recovery, so an unsent draft in a temp chat would outlive the chat itself.
const isDraftPersistableContext = (contextId) => {
  if (!contextId) return false;
  try {
    const chat = getStores().useChatStore.getState().chats.find(c => c.id === contextId);
    return chat?.flags?.isTemporary !== true;
  } catch (_) {
    return true;
  }
};

// AbortController for the in-flight /json/conversations/calculate-credits
// request. Each new calculation aborts the previous one so the backend stops
// doing expensive intent/context/preprocess work for a stale payload.
let creditCalculationAbortController = null;

const useChatInputStore = create((set, get) => ({
  // Input state
  currentContextId: null, // Track current context (conversationId, pageId, etc)
  inputStates: {}, // Map of contextId to input state
  pendingAutoSend: null, // Pending message to auto-send when chat page loads
  highlightedMissingParameterIds: [], // Parameter IDs to highlight with glow animation when validation fails
  // Focus request signal: ChatInput components listen and focus their TextInput when contextId matches and tick changes.
  focusRequest: null, // { contextId, tick }
  // : todo :
  // expiration date for input states so they fall off after a certain time
  
  // Change listeners - functions that get called when state changes
  changeListeners: [],

  // Always visible parameters
  alwaysVisibleParameters: [
    {
      id: "files",
      name: "files",
      type: "array",
      value: null,
      modelId: null,
      description: "Files to upload",
      validation: {
        required: false,
        options: [
          { value: "file-upload", icon: "arrow-up-from-arc", label: "Upload a file", description: "Documents, photos, audio, or video · Max 50 MB" },
          { value: "file-screenshot", icon: "camera", label: "Take a screenshot", description: "Capture your screen" },
          { value: "file-library", icon: "cloud-plus", label: "Select from library", description: "Select from library" }
        ],
        default: null
      },
      ui: {
        displayName: "Files", 
        description: "Files to upload",
        category: "Output",
        order: 1,
        component: "action",
        icon: "paperclip"
      }
    },
    {
      id: "tools",
      name: "tools",
      type: "array",
      value: null,
      modelId: null,
      description: "Select tools",
      validation: {
        required: false,
        options: [
          {
            value: "search-web",
            icon: "earth-americas",
            label: "Web search",
            description: "Get real-time info from the internet",
            flags: {
              isPlusOnly: true,
            },
            credits: {
              baseCost: 1,
              multiplier: 1,
              discount: 0,
            },
          },
          {
            value: "study-and-learn",
            icon: "book",
            label: "Study and learn",
            description: "Quiz mode to test your knowledge",
          },
        ],
        default: null
      },
      ui: {
        displayName: "Tools",
        description: "Select tools",
        category: "Search",
        order: 2,
        component: "multiselect",
        icon: "sliders-simple"
      }
    },
    /* // !!! TESTING !!!
    {
      id: "model_select",
      name: "model_select",
      type: "string",
      value: null,
      description: "Select a model",
      validation: {
        required: false,
        options: [
          { value: "gpt-4", icon: "sparkles", label: "GPT-4", description: "Most capable model" },
          { value: "gpt-3.5", icon: "bolt", label: "GPT-3.5", description: "Fast and efficient" }
        ],
        default: null
      },
      ui: {
        displayName: "Model Selection",
        description: "Choose your model",
        category: "Models",
        order: 2,
        component: "select",
        icon: "cube"
      }
    },
    {
      id: "temperature",
      name: "temperature",
      type: "number",
      value: 0.7,
      description: "Controls randomness",
      validation: {
        number: {
          min: 0,
          max: 2,
          step: 0.1
        },
        required: false,
        default: 0.7
      },
      ui: {
        displayName: "Temperature",
        description: "Higher values make output more random",
        category: "Generation",
        order: 3,
        component: "number",
        unit: "°",
        icon: "thermometer"
      }
    },
    {
      id: "creativity",
      name: "creativity",
      type: "number",
      value: 50,
      description: "Creativity slider",
      validation: {
        number: {
          min: 0,
          max: 100,
          step: 1
        },
        required: false,
        default: 50
      },
      ui: {
        displayName: "Creativity",
        description: "Adjust creativity level",
        category: "Generation",
        order: 4,
        component: "slider",
        icon: "sparkles"
      }
    },
    {
      id: "audio_input",
      name: "audio_input",
      type: "file",
      value: null,
      description: "Upload audio file",
      validation: {
        required: false,
        mimeTypes: ["audio/mp3", "audio/wav"],
        maxSize: 10485760 // 10MB
      },
      ui: {
        displayName: "Audio Input",
        description: "Upload an audio file",
        category: "Input",
        order: 5,
        component: "audio",
        icon: "microphone"
      }
    },
    {
      id: "stream_mode",
      name: "stream_mode",
      type: "boolean",
      value: true,
      description: "Enable streaming mode",
      validation: {
        required: false,
        default: true
      },
      ui: {
        displayName: "Streaming Mode",
        description: "Toggle streaming responses",
        category: "Output",
        order: 6,
        component: "toggle",
        icon: "play"
      }
    },
    {
      id: "custom_prompt",
      name: "custom_prompt",
      type: "string",
      value: "",
      description: "Custom system prompt",
      validation: {
        required: false,
        maxLength: 1000
      },
      ui: {
        displayName: "System Prompt",
        description: "Enter a custom system prompt",
        category: "Advanced",
        order: 7,
        component: "textarea",
        icon: "code"
      }
    },
    {
      id: "search_filters",
      name: "search_filters",
      type: "object",
      value: {
        include: "",
        exclude: ""
      },
      description: "Search filters",
      validation: {
        required: false,
        fields: [
          {
            key: "include",
            label: "Include Terms",
            placeholder: "Terms to include, comma separated"
          },
          {
            key: "exclude",
            label: "Exclude Terms", 
            placeholder: "Terms to exclude, comma separated"
          }
        ]
      },
      ui: {
        displayName: "Search Filters",
        description: "Configure search terms",
        category: "Search",
        order: 8,
        component: "textarea-multi",
        icon: "filter"
      }
    }*/
  ],

  // Calculate credits for a message
  calculateMessageCredits: async ({ conversationId, collectionId, userId, files, formattedMessage }) => {    
    const contextId = get().currentContextId;
    if (!contextId) return;

    // Check for any uploading files
    if (files?.some(file => file.isUploading || (file.id && file.id.includes('uploading')))) {
      return;
    }

    // Composer contexts like `topic-start-<slug>` are input-state keys, not Mongo ids.
    const apiConversationId = conversationId
      && !String(conversationId).includes('new')
      && /^[a-f0-9]{24}$/i.test(String(conversationId))
      ? conversationId
      : undefined;

    // Abort any in-flight calculation so the backend stops doing expensive
    // intent/context/preprocess work for a now-stale payload. Only the most
    // recent request is allowed to write results.
    if (creditCalculationAbortController) {
      creditCalculationAbortController.abort();
    }
    const controller = new AbortController();
    creditCalculationAbortController = controller;

    try {
      const result = await chatsApi.calculateCredits({
        conversationId: apiConversationId,
        collectionId,
        userId,
        files,
        formattedMessage,
        signal: controller.signal
      });

      // A newer request may have started while we were awaiting; if so,
      // leave state writes to it.
      if (controller.signal.aborted) return;
      if (creditCalculationAbortController === controller) {
        creditCalculationAbortController = null;
      }

      // Update user credits in auth store
      if (result.userCredits) {
        getStores().useAuthStore.getState().updateUserCredits({
          monthlyAllotmentTotal: result.userCredits.monthlyAllotmentTotal,
          monthlyAllotmentRemaining: result.userCredits.monthlyAllotmentRemaining,
          purchasedCreditsAvailable: result.userCredits.purchasedCreditsAvailable,
          paidCreditsAvailable: result.userCredits.paidCreditsAvailable,
          totalAvailableCredits: result.userCredits.totalAvailableCredits
        });
      } else {
        // If API didn't return a userCredits payload, proactively fetch user to stay in sync
        try {
          const authStore = getStores().useAuthStore.getState();
          if (authStore?.fetchUserCredits) {
            authStore.fetchUserCredits();
          }
        } catch (e) {
          // Silently fail - credit refresh is best effort
        }
      }

      set(state => ({
        inputStates: {
          ...state.inputStates,
          [contextId]: {
            ...state.inputStates[contextId],
            creditCalculation: {
              isCalculating: false,
              error: null,
              cost: result?.cost?.total ?? result?.cost?.authorizationTotal ?? null,
              authorizationCost: result?.cost?.authorizationTotal ?? result?.cost?.total ?? null,
              variableBilling: !!result?.cost.variableBilling
            }
          }
        }
      }));

      return result;
    } catch (error) {
      // Superseded by a newer request — drop silently without surfacing an
      // error or clearing the displayed cost.
      if (isAbortedError(error) || controller.signal.aborted) {
        if (creditCalculationAbortController === controller) {
          creditCalculationAbortController = null;
        }
        return;
      }
      if (creditCalculationAbortController === controller) {
        creditCalculationAbortController = null;
      }
      set(state => ({
        inputStates: {
          ...state.inputStates,
          [contextId]: {
            ...state.inputStates[contextId],
            creditCalculation: {
              isCalculating: false,
              error: error.message || 'Failed to calculate credits',
              cost: null,
              authorizationCost: null,
            }
          }
        }
      }));
      throw error;
    }
  },

  // Reset credit calculation state
  resetCreditCalculation: () => {
    const contextId = get().currentContextId;
    if (!contextId) return;

    // Cancel any in-flight calculation so its (now stale) result can't
    // land after the reset and clobber the cleared state.
    if (creditCalculationAbortController) {
      creditCalculationAbortController.abort();
      creditCalculationAbortController = null;
    }

    // Skip if already in default state — avoids creating new state
    // and triggering unnecessary re-renders during initial mount
    const current = get().inputStates[contextId]?.creditCalculation;
    if (
      current
      && !current.isCalculating
      && current.error === null
      && current.cost === null
      && current.authorizationCost == null
    ) {
      return;
    }

    set(state => ({
      inputStates: {
        ...state.inputStates,
        [contextId]: {
          ...state.inputStates[contextId],
          creditCalculation: {
            isCalculating: false,
            error: null,
            cost: null,
            authorizationCost: null,
          }
        }
      }
    }));
  },

  // Add mapping for action placeholders
  actionPlaceholders: {
    "create-image": "Describe an image",
    "search": "Search the web",
  },

  // Update function to set placeholder based on action
  setInputPlaceholder: (action, placeholder) => {
    const { currentContextId, inputStates, actionPlaceholders } = get();
    if (!currentContextId) return;
    const currentState = inputStates[currentContextId] || {};

    const nextPlaceholder = action 
      ? actionPlaceholders[action] || actionPlaceholders.default 
      : (placeholder ? placeholder : "Message Gab AI");

    if (currentState.inputPlaceholder === nextPlaceholder) {
      return;
    }

    const result = set(state => {
      const contextId = state.currentContextId;
      if (!contextId) return state;
      const curr = state.inputStates[contextId];

      return {
        inputStates: {
          ...state.inputStates,
          [contextId]: {
            ...curr,
            inputPlaceholder: nextPlaceholder
          }
        }
      };
    });
    
    get().triggerChangeListeners('setInputPlaceholder', { action, placeholder });
    return result;
  },

  // Provide action-driven dynamic parameters as a reusable method
  getActionParameters: (baseState) => {
    const selectedAction = baseState?.selectedParameters?.action;
    let theseParameters = [];
    if (selectedAction === 'search') {
      theseParameters = [{
        id: "sources",
        name: "sources",
        type: "object",
        modelId: null,
        value: {
          include: baseState?.selectedParameters?.sources?.include || "",
          exclude: baseState?.selectedParameters?.sources?.exclude || ""
        },
        description: "Sources to include or exclude from search",
        validation: {
          required: false,
          fields: [
            {
              key: "include",
              label: "Include Sources",
              placeholder: "Enter URLs to include, separated by commas",
            },
            {
              key: "exclude",
              label: "Exclude Sources",
              placeholder: "Enter URLs to exclude, separated by commas",
            }
          ]
        },
        ui: {
          displayName: "Search Sources",
          description: "If no sources are specified, search will be performed across the entire web",
          category: "Search",
          order: 2,
          component: "textarea-multi",
          icon: "book-bookmark",
          text: "Sources"
        }
      }];
    }

    // Dynamic parameters based on 'tools' selection
    const selectedSearchAndToolsRaw = baseState?.selectedParameters?.['tools'];
    const selectedSearchAndTools = Array.isArray(selectedSearchAndToolsRaw) ? selectedSearchAndToolsRaw : (selectedSearchAndToolsRaw ? [selectedSearchAndToolsRaw] : []);

        if (selectedSearchAndTools.includes('search-web')) {
      theseParameters = [
        ...theseParameters,
        {
          id: "tools_indicator_web",
          name: "tools_indicator_web",
          type: "indicator",
          value: null,
          modelId: null,
          description: "Web search selected",
          validation: {
            required: false
          },
          ui: {
            displayName: "Search",
            description: "Click to remove",
            category: "Search",
            order: 3,
            component: "indicator",
            icon: "earth-americas",
            text: "Search",
            clearParamId: "tools",
            clearValue: "search-web"
          }
        },
        {
          id: "search_date_range",
          name: "search_date_range",
          type: "array",
          value: baseState?.selectedParameters?.search_date_range || null,
          modelId: null,
          description: "Select a start and end date for the date range",
          flags: {
            isPlusOnly: false,
            isAdminOnly: false,
            isVisibleToUsers: true,
          },
          credits: {
            baseCost: 0,
            perUnitCost: 0,
            multiplier: 1.0,
          },
          validation: {
            array: {
              minItems: 2,
              maxItems: 2,
              uniqueItems: false
            },
            required: false
          },
          ui: {
            displayName: "Date Range",
            icon: "calendar-range",
            component: "daterange",
            category: "Filters",
            order: 1
          }
        },
        {
          id: "search_filters",
          name: "search_filters",
          type: "object",
          modelId: null,
          value: {
            keywords: baseState?.selectedParameters?.search_filters?.keywords || "",
            domains: baseState?.selectedParameters?.search_filters?.domains || ""
          },
          description: "Search filters",
          validation: {
            required: false,
            fields: [
              {
                key: "keywords",
                label: "Keywords / search terms",
                placeholder: "Words or phrases, comma separated",
                maxLength: 256
              },
              {
                key: "domains",
                label: "Domain filtering",
                placeholder: "e.g. example.com, docs.site.com, -twitter.com (max 20)",
                maxLength: 256
              }
            ]
          },
          ui: {
            displayName: "Search Filters",
            description: "Keywords: enter search terms (comma separated).\nDomain filtering: enter domains/websites (comma separated). To include, list domains normally. To exclude, prefix a minus (e.g. -example.com). Maximum 20 domains.",
            category: "Search",
            order: 8,
            component: "textarea-multi",
            icon: "filter-list"
          }
        }
      ];
    }

    if (selectedSearchAndTools.includes('study-and-learn')) {
      theseParameters = [
        ...theseParameters,
        {
          id: "tools_indicator_study",
          name: "tools_indicator_study",
          type: "indicator",
          value: null,
          modelId: null,
          description: "Study and learn selected",
          validation: {
            required: false
          },
          ui: {
            displayName: "Study",
            description: "Click to remove",
            category: "Learning",
            order: 4,
            component: "indicator",
            icon: "book",
            text: "Study",
            clearParamId: "tools",
            clearValue: "study-and-learn"
          }
        }
      ];
    }

    // Append parameters defined by selected models (ignore any with name "prompt")
    // Each model's parameters are now namespaced with modelId to allow same-named params across models
    try {
      const modelStore = getStores().useModelStore.getState();
      const selectedModelIds = Array.isArray(baseState?.selectedModelIds)
        ? baseState.selectedModelIds
        : (baseState?.selectedModelIds ? [baseState.selectedModelIds] : []);

      const modelParameters = [];

      selectedModelIds.forEach((modelId) => {
        const model = (modelStore.getModelById ? modelStore.getModelById(modelId) : null) || modelStore.models?.[modelId];
        if (!model || !model.parameters) return;

        // Parameters are now a flat array
        const paramsList = Array.isArray(model.parameters) ? model.parameters : [];

        // Track seen param names within this model to avoid duplicates within same model
        const seenInModel = new Set();

        paramsList.forEach((p) => {
          if (!p || typeof p !== 'object') return;
          const name = p.name || p.id;
          if (!name) return;
          if (String(name).toLowerCase() === 'prompt') return; // ignore prompt
          if (seenInModel.has(name)) return; // dedupe within same model only
          seenInModel.add(name);

          // Use composite key for model-specific parameters: "modelId:paramName"
          const compositeKey = `${modelId}:${name}`;
          const selectedValue = baseState?.selectedParameters?.[compositeKey];
          const ui = {
            ...(p.ui || {}),
            icon: (p.ui && (p.ui.icon || p.ui.displayIcon)) || undefined,
          };
          if (ui.displayIcon) delete ui.displayIcon;

          modelParameters.push({
            id: compositeKey, // Use composite key as id for uniqueness
            name,
            type: p.type,
            value: selectedValue !== undefined ? selectedValue : (p.value ?? null),
            description: p.description,
            validation: p.validation || {},
            ui,
            modelId: modelId,
          });
        });
      });

      if (modelParameters.length) {
        theseParameters = [
          ...theseParameters,
          ...modelParameters,
        ];
      }
    } catch (e) {
      // Silently fail - model parameters are optional
    }
    
    const allParameters = [
      ...get().alwaysVisibleParameters,
      ...theseParameters,
    ];

    return allParameters;
  },

  // Pass contextId when a specific composer must be read; split views may
  // mount multiple inputs that otherwise race the global currentContextId.
  getCurrentState: (contextId) => {
    contextId = contextId || get().currentContextId;
    const baseState = get().inputStates[contextId] || {
      text: '',
      files: [],
      selectedParameters: {},
      selectedButtons: [],
      selectedModelIds: [],
      selectedAgentIds: [],
      inputPlaceholder: "Message Gab AI",
      creditCalculation: {
        isCalculating: false,
        error: null,
        cost: null
      },
      banners: [], // Initialize empty banners array
      quote: '',
      dismissedBanners: [],
      editFileIds: [], // File IDs to edit (e.g., for inpainting)
    };

    // Combine always visible parameters with any dynamic parameters
    const allParameters = get().getActionParameters(baseState);

    return {
      ...baseState,
      parameters: allParameters
    };
  },

  _getDefaultSelectedParameters: () => (
    getStores().useUIStore.getState().webSearchEnabled ? { tools: ['search-web'] } : {}
  ),

  _getDefaultModelIds: () => {
    const raw = getStores().useAuthStore.getState().user?.defaultModel?.text;
    if (!raw) return [];
    const id = typeof raw === 'string' ? raw : raw.id;
    if (!id) return [];

    const model = getStores().useModelStore.getState().getModelById?.(id) || getStores().useModelStore.getState().models?.[id];
    if (!model) return [];

    const isUsable = model.flags?.isEnabled === true &&
      model.flags?.isDeprecated !== true &&
      !model.archivedAt &&
      !model.deletedAt;

    return isUsable ? [id] : [];
  },

  // If a context was created before ui-storage finished hydrating, re-apply
  // the persisted Search Web preference once (without overwriting a manual
  // toggle the user already made in this session).
  _scheduleWebSearchHydrationSeed: (contextId) => {
    if (!contextId) return;
    const persistApi = getStores().useUIStore.persist;
    if (!persistApi || typeof persistApi.hasHydrated !== 'function') return;
    if (persistApi.hasHydrated()) return;

    const unsub = persistApi.onFinishHydration?.(() => {
      unsub?.();
      const enabled = getStores().useUIStore.getState().webSearchEnabled;
      if (!enabled) return;

      // Patch this context directly — currentContextId may have changed by
      // the time hydration finishes, so setParameter would miss the target.
      set((state) => {
        const ctx = state.inputStates?.[contextId];
        if (!ctx) return state;

        const tools = ctx.selectedParameters?.tools;
        const hasSearch = Array.isArray(tools) && tools.includes('search-web');
        if (hasSearch) return state;

        // Only seed when tools were never explicitly set for this context.
        if (tools !== undefined) return state;

        return {
          inputStates: {
            ...state.inputStates,
            [contextId]: {
              ...ctx,
              selectedParameters: {
                ...ctx.selectedParameters,
                tools: ['search-web'],
              },
            },
          },
        };
      });
    });
  },

  // Set context for input state
  setContext: (contextId) => {
    if (!contextId) return;
    const { currentContextId } = get();
    // Skip if context is unchanged
    if (currentContextId === contextId) {
      return;
    }
    const defaultModelIds = get()._getDefaultModelIds();
    const defaultSelectedParameters = get()._getDefaultSelectedParameters();
    const creatingNewContext = !get().inputStates?.[contextId];
    const restoredDraft = creatingNewContext && isDraftPersistableContext(contextId)
      ? readComposerDraft(contextId)
      : '';
    const result = set(state => ({
      currentContextId: contextId,
      inputStates: {
        ...state.inputStates,
        [contextId]: state.inputStates[contextId] || {
          text: restoredDraft,
          files: [],
          selectedParameters: defaultSelectedParameters,
          selectedButtons: [],
          selectedModelIds: defaultModelIds,
          selectedAgentIds: [],
          userExplicitAutoMode: false,
          inputPlaceholder: "Message Gab AI",
          creditCalculation: {
            isCalculating: false,
            error: null,
            cost: null
          },
          banners: [],
          quote: '',
          dismissedBanners: [],
          editFileIds: [],
        }
      }
    }));

    if (creatingNewContext) {
      get()._scheduleWebSearchHydrationSeed(contextId);
    }
    
    get().triggerChangeListeners('setContext', { contextId });
    return result;
  },

  // Actions
  setText: (text) => {
    const { currentContextId, inputStates } = get();
    if (!currentContextId) return;
    const prevText = inputStates[currentContextId]?.text ?? '';
    if (prevText === (text ?? '')) {
      // No change
      return;
    }
    const result = set(state => {
      const contextId = state.currentContextId;
      if (!contextId) return state;

      return {
        inputStates: {
          ...state.inputStates,
          [contextId]: {
            ...state.inputStates[contextId],
            text
          }
        }
      };
    });

    // Trigger change listeners
    get().triggerChangeListeners('setText', { text });
    if (isDraftPersistableContext(currentContextId)) {
      scheduleSaveComposerDraft(currentContextId, text);
    }
    return result;
  },

  setQuote: (quote) => {
    const { currentContextId, inputStates } = get();
    if (!currentContextId) return;
    const prevQuote = inputStates[currentContextId]?.quote ?? '';
    if (prevQuote === (quote ?? '')) {
      return;
    }
    const result = set(state => {
      const contextId = state.currentContextId;
      if (!contextId) return state;

      return {
        inputStates: {
          ...state.inputStates,
          [contextId]: {
            ...state.inputStates[contextId],
            quote: quote ?? ''
          }
        }
      };
    });

    get().triggerChangeListeners('setQuote', { quote });
    return result;
  },

  setEditFileIds: (editFileIds) => {
    const { currentContextId, inputStates } = get();
    if (!currentContextId) return;
    const prevEditFileIds = inputStates[currentContextId]?.editFileIds || [];
    const nextEditFileIds = Array.isArray(editFileIds) ? editFileIds : [];
    const arraysEqual = prevEditFileIds.length === nextEditFileIds.length && 
      prevEditFileIds.every((v, i) => v === nextEditFileIds[i]);
    if (arraysEqual) {
      return;
    }
    const result = set(state => {
      const contextId = state.currentContextId;
      if (!contextId) return state;

      return {
        inputStates: {
          ...state.inputStates,
          [contextId]: {
            ...state.inputStates[contextId],
            editFileIds: nextEditFileIds
          }
        }
      };
    });

    get().triggerChangeListeners('setEditFileIds', { editFileIds: nextEditFileIds });
    return result;
  },

  setFiles: (files) => {
    const { currentContextId, inputStates } = get();
    if (!currentContextId) return;
    const prevFiles = inputStates[currentContextId]?.files || [];
    const normalize = (arr) => (Array.isArray(arr) ? arr.map(f => (typeof f === 'object' ? f?.id : f)) : []);
    const prevNorm = normalize(prevFiles);
    const nextNorm = normalize(files);
    const arraysEqual = prevNorm.length === nextNorm.length && prevNorm.every((v, i) => v === nextNorm[i]);
    if (arraysEqual) {
      return;
    }

    const result = set(state => {
      const contextId = state.currentContextId;
      if (!contextId) return state;

      return {
        inputStates: {
          ...state.inputStates,
          [contextId]: {
            ...state.inputStates[contextId],
            files
          }
        }
      };
    });
    
    get().triggerChangeListeners('setFiles', { files });
    return result;
  },

  addFile: (file) => {
    if (typeof file === 'string') {
      return get().addFileById(file);
    }

    const result = set(state => {
      const contextId = state.currentContextId;
      if (!contextId) return state;
      const currentState = state.inputStates[contextId];

      // Don't add if already exists
      if (currentState.files.some(existingFile => 
        (typeof existingFile === 'object' && existingFile.id === file.id) ||
        (typeof existingFile === 'string' && existingFile === file.id)
      )) {
        return state;
      }

      return {
        inputStates: {
          ...state.inputStates,
          [contextId]: {
            ...currentState,
            files: [...currentState.files, file]
          }
        }
      };
    });
    
    get().triggerChangeListeners('addFile', { file });
    return result;
  },

  addFilesToContext: (contextId, filesToAdd) => {
    if (!contextId) return;
    const incomingFiles = (Array.isArray(filesToAdd) ? filesToAdd : [filesToAdd]).filter(Boolean);
    if (incomingFiles.length === 0) return;

    const getFileId = (file) => (typeof file === 'object' ? file?.id : file);

    const result = set(state => {
      const defaultModelIds = get()._getDefaultModelIds();
      const currentState = state.inputStates[contextId] || {
        text: '',
        files: [],
        selectedParameters: {},
        selectedButtons: [],
        selectedModelIds: defaultModelIds,
        selectedAgentIds: [],
        userExplicitAutoMode: false,
        inputPlaceholder: "Message Gab AI",
        creditCalculation: {
          isCalculating: false,
          error: null,
          cost: null
        },
        banners: [],
        quote: '',
        dismissedBanners: [],
        editFileIds: [],
      };

      const currentFiles = Array.isArray(currentState.files) ? currentState.files : [];
      let nextFiles = currentFiles;
      let didChange = false;

      incomingFiles.forEach((file) => {
        const fileId = getFileId(file);
        if (!fileId) return;
        const alreadyExists = nextFiles.some((existingFile) => getFileId(existingFile) === fileId);
        if (alreadyExists) return;
        nextFiles = [...nextFiles, file];
        didChange = true;
      });

      if (!didChange) return state;

      return {
        inputStates: {
          ...state.inputStates,
          [contextId]: {
            ...currentState,
            files: nextFiles,
          }
        }
      };
    });

    get().triggerChangeListeners('addFilesToContext', { contextId, files: incomingFiles });
    return result;
  },

  addFileById: (fileId) => {
    const result = set(state => {
      const contextId = state.currentContextId;
      if (!contextId) return state;
      const currentState = state.inputStates[contextId];

      // Don't add if already exists
      if (currentState.files.some(file => 
        (typeof file === 'string' && file === fileId) || 
        (typeof file === 'object' && file.id === fileId)
      )) {
        return state;
      }

      return {
        inputStates: {
          ...state.inputStates,
          [contextId]: {
            ...currentState,
            files: [...currentState.files, fileId]
          }
        }
      };
    });
    
    get().triggerChangeListeners('addFileById', { fileId });
    return result;
  },

  removeFile: (fileToRemove) => {
    if (typeof fileToRemove === 'string') {
      return get().removeFileById(fileToRemove);
    }

    const result = set(state => {
      const contextId = state.currentContextId;
      if (!contextId) return state;
      const currentState = state.inputStates[contextId];

      return {
        inputStates: {
          ...state.inputStates,
          [contextId]: {
            ...currentState,
            files: currentState.files.filter(file => file !== fileToRemove)
          }
        }
      };
    });
    
    get().triggerChangeListeners('removeFile', { fileToRemove });
    return result;
  },

  removeFileById: (fileId) => {
    const result = set(state => {
      const contextId = state.currentContextId;
      if (!contextId) return state;
      const currentState = state.inputStates[contextId];

      return {
        inputStates: {
          ...state.inputStates,
          [contextId]: {
            ...currentState,
            files: currentState.files.filter(file => 
              (typeof file === 'string' && file !== fileId) ||
              (typeof file === 'object' && file.id !== fileId)
            )
          }
        }
      };
    });
    
    get().triggerChangeListeners('removeFileById', { fileId });
    return result;
  },

  // Remove a file by ID across ALL input contexts. Used to clean up temporary
  // upload placeholders when an upload fails — by the time the request errors
  // out the user may have switched conversations, so scoping to the current
  // context would leak a stuck `isUploading` placeholder that disables Send.
  removeFileByIdFromAllContexts: (fileId) => {
    if (!fileId) return;

    const result = set(state => {
      const newInputStates = { ...state.inputStates };
      let foundAndRemoved = false;

      Object.keys(newInputStates).forEach(contextId => {
        const contextState = newInputStates[contextId];
        if (!contextState?.files?.length) return;

        const filteredFiles = contextState.files.filter(file =>
          (typeof file === 'string' && file !== fileId) ||
          (typeof file === 'object' && file?.id !== fileId)
        );

        if (filteredFiles.length !== contextState.files.length) {
          newInputStates[contextId] = {
            ...contextState,
            files: filteredFiles,
          };
          foundAndRemoved = true;
        }
      });

      if (!foundAndRemoved) return state;

      return {
        inputStates: newInputStates,
      };
    });

    get().triggerChangeListeners('removeFileByIdFromAllContexts', { fileId });
    return result;
  },

  // Replace a temporary upload placeholder wherever it was originally added.
  // Uploads may finish after the user switches conversations, so this must not
  // depend on currentContextId.
  replaceFileByIdInAllContexts: (fileId, replacement) => {
    if (!fileId || !replacement) return false;

    let didReplace = false;
    const result = set(state => {
      const newInputStates = { ...state.inputStates };

      Object.keys(newInputStates).forEach(contextId => {
        const contextState = newInputStates[contextId];
        if (!contextState?.files?.length) return;

        const getFileId = (file) => (typeof file === 'object' ? file?.id : file);
        const placeholderIndex = contextState.files.findIndex(file => getFileId(file) === fileId);
        if (placeholderIndex === -1) return;

        const replacementId = getFileId(replacement);
        const duplicateIndex = contextState.files.findIndex(
          (file, index) => index !== placeholderIndex && replacementId && getFileId(file) === replacementId
        );
        const nextFiles = [...contextState.files];
        if (duplicateIndex !== -1) nextFiles.splice(placeholderIndex, 1);
        else nextFiles[placeholderIndex] = replacement;

        newInputStates[contextId] = {
          ...contextState,
          files: nextFiles,
        };
        didReplace = true;
      });

      return didReplace ? { inputStates: newInputStates } : state;
    });

    if (didReplace) {
      get().triggerChangeListeners('replaceFileByIdInAllContexts', { fileId, replacement });
    }
    return didReplace || result === true;
  },

  // Update a file by ID across all input contexts
  // This is used to update file processing status when the backend signals completion
  updateFileById: (fileId, updates) => {
    if (!fileId || !updates) return;
    
    const result = set(state => {
      const newInputStates = { ...state.inputStates };
      let foundAndUpdated = false;
      
      // Search through all contexts for the file
      Object.keys(newInputStates).forEach(contextId => {
        const contextState = newInputStates[contextId];
        if (!contextState?.files?.length) return;
        
        const fileIndex = contextState.files.findIndex(file => 
          (typeof file === 'object' && file?.id === fileId)
        );
        
        if (fileIndex !== -1) {
          const existingFile = contextState.files[fileIndex];
          const updatedFiles = [...contextState.files];
          updatedFiles[fileIndex] = {
            ...existingFile,
            ...updates,
          };
          
          newInputStates[contextId] = {
            ...contextState,
            files: updatedFiles,
          };
          foundAndUpdated = true;
        }
      });
      
      if (!foundAndUpdated) {
        return state; // No changes needed
      }
      
      return {
        inputStates: newInputStates,
      };
    });
    
    get().triggerChangeListeners('updateFileById', { fileId, updates });
    return result;
  },

  // Pass { syncWebSearchPreference: false } for programmatic seeding (e.g.
  // collection defaults) that shouldn't overwrite the user's persisted choice.
  setParameter: (paramId, value, options = {}) => {
    const result = set(state => {
      const contextId = state.currentContextId;
      if (!contextId) return state;
      const currentState = state.inputStates[contextId];

      const nextSelected = { ...currentState.selectedParameters };
      if (value === null || value === undefined) {
        delete nextSelected[paramId];
      } else {
        nextSelected[paramId] = value;
      }

      return {
        inputStates: {
          ...state.inputStates,
          [contextId]: {
            ...currentState,
            selectedParameters: nextSelected,
            // Leave creditCalculation unchanged; API will update cost
          }
        }
      };
    });

    // Keep the persisted web-search preference in sync with every tools
    // change, so new contexts (seeded via _getDefaultSelectedParameters)
    // don't silently re-enable web search after the user turned it off.
    if (paramId === 'tools' && options.syncWebSearchPreference !== false) {
      const tools = Array.isArray(value) ? value : (value ? [value] : []);
      getStores().useUIStore.getState().setWebSearchEnabled(tools.includes('search-web'));
    }

    get().triggerChangeListeners('setParameter', { paramId, value });
    return result;
  },

  toggleButton: (buttonId) => {
    const result = set(state => {
      const contextId = state.currentContextId;
      if (!contextId) return state;
      const currentState = state.inputStates[contextId];
      
      const newButtons = currentState.selectedButtons.includes(buttonId)
        ? currentState.selectedButtons.filter(id => id !== buttonId)
        : [...currentState.selectedButtons, buttonId];

      return {
        inputStates: {
          ...state.inputStates,
          [contextId]: {
            ...currentState,
            selectedButtons: newButtons
          }
        }
      };
    });
    
    get().triggerChangeListeners('toggleButton', { buttonId });
    return result;
  },

  setSelectedModels: (modelIds, options = {}) => {
    const clearAgentsWhenSettingModels = options.clearAgents !== false;
    // Programmatic selections (default-model bootstrap, history seeding) pass
    // isSeed so they don't count as an explicit user choice. Explicit choices
    // set modelSelectionTouched, which blocks any later automatic seeding
    // from overriding what the user picked.
    const isSeed = options.isSeed === true;
    const { currentContextId, inputStates } = get();
    if (!currentContextId) return;
    const currentState = inputStates[currentContextId] || {};

    const extractIds = (input) => {
      if (!input) return [];
      if (Array.isArray(input)) {
        return input.map(item => (typeof item === 'object' && item?.id ? item.id : item));
      }
      return [typeof input === 'object' && input?.id ? input.id : input];
    };
    const uniqueModelIds = [...new Set(extractIds(modelIds))];

    const prev = currentState.selectedModelIds || [];
    const arraysEqual = prev.length === uniqueModelIds.length && prev.every((v, i) => v === uniqueModelIds[i]);
    
    // Debug logging to track when model selection changes
    if (arraysEqual) {
      return;
    }

    const result = set(state => {
      const contextId = state.currentContextId;
      if (!contextId) return state;
      const curr = state.inputStates[contextId];

      // Build allowed parameter names for the new model selection
      let filteredSelectedParameters = curr?.selectedParameters || {};
      try {
        const effectiveBaseState = {
          ...curr,
          selectedModelIds: uniqueModelIds,
        };
        const allParams = get().getActionParameters(effectiveBaseState) || [];
        const allowedNames = new Set(
          allParams
            .map(p => (p && (p.id || p.name)))
            .filter(Boolean)
        );
        filteredSelectedParameters = Object.fromEntries(
          Object.entries(curr?.selectedParameters || {}).filter(([k]) => allowedNames.has(k))
        );
      } catch (e) {
        // Fallback to existing parameters if pruning fails
        filteredSelectedParameters = curr?.selectedParameters || {};
      }

      return {
        inputStates: {
          ...state.inputStates,
          [contextId]: {
            ...curr,
            selectedModelIds: uniqueModelIds,
            selectedParameters: filteredSelectedParameters,
            userExplicitAutoMode: !isSeed && uniqueModelIds.length === 0,
            ...(isSeed ? {} : { modelSelectionTouched: true }),
            // Dropping agents on explicit model choice fixes stale agentIds after the user
            // picks a model; optional off for programmatic defaults (see ChatInput bootstrap).
            ...(uniqueModelIds.length > 0 && clearAgentsWhenSettingModels
              ? { selectedAgentIds: [] }
              : {}),
          }
        }
      };
    });
    
    get().triggerChangeListeners('setSelectedModels', { modelIds });
    return result;
  },

  // Seed a conversation context's model selection without it counting as an
  // explicit user choice. Used to:
  //   1. Carry the home composer's selection into a just-created conversation
  //      (otherwise picking e.g. GPT Image 2 silently reverts to the default
  //      text model / Auto after the first message).
  //   2. Restore the last-used model when reopening a conversation after a
  //      refresh (input states are not persisted).
  // No-ops if the user already made an explicit selection (including explicit
  // Auto) in that context, or if none of the models are currently usable.
  // Returns true when seeding is settled (applied, skipped, or permanently
  // unavailable); false when models are not in the store yet and the caller
  // should retry after models load.
  seedConversationModels: (contextId, modelIds) => {
    if (!contextId) return true;
    const ids = (Array.isArray(modelIds) ? modelIds : [modelIds])
      .map(item => (typeof item === 'object' && item?.id ? item.id : item))
      .filter(Boolean);
    if (ids.length === 0) return true;

    const modelStore = getStores().useModelStore.getState();
    const uniqueIds = [...new Set(ids)];
    const missingFromStore = uniqueIds.filter(id => {
      const model = modelStore.getModelById?.(id) || modelStore.models?.[id];
      return !model;
    });
    const usableIds = uniqueIds.filter(id => {
      const model = modelStore.getModelById?.(id) || modelStore.models?.[id];
      return model &&
        model.flags?.isEnabled === true &&
        model.flags?.isDeprecated !== true &&
        !model.archivedAt &&
        !model.deletedAt;
    });
    if (usableIds.length === 0) {
      // Retry only when the model simply hasn't been loaded into the store yet
      // (refresh race). If every id is known but unusable, settle.
      return missingFromStore.length === 0;
    }

    let didSeed = false;
    let skipped = false;
    set(state => {
      const existing = state.inputStates[contextId];
      if (existing && (existing.modelSelectionTouched || existing.userExplicitAutoMode)) {
        skipped = true;
        return state;
      }
      const prev = existing?.selectedModelIds || [];
      const unchanged = prev.length === usableIds.length && prev.every((v, i) => v === usableIds[i]);
      if (unchanged) {
        skipped = true;
        return state;
      }

      didSeed = true;
      const base = existing || {
        text: '',
        files: [],
        selectedParameters: {},
        selectedButtons: [],
        selectedModelIds: [],
        selectedAgentIds: [],
        userExplicitAutoMode: false,
        inputPlaceholder: "Message Gab AI",
        creditCalculation: {
          isCalculating: false,
          error: null,
          cost: null
        },
        banners: [],
        quote: '',
        dismissedBanners: [],
        editFileIds: [],
      };

      return {
        inputStates: {
          ...state.inputStates,
          [contextId]: {
            ...base,
            selectedModelIds: usableIds,
          }
        }
      };
    });

    // Only notify listeners when the seeded context is the active one —
    // triggerChangeListeners reports getCurrentState(), so firing it while
    // seeding a background context (e.g. just-created conversation while the
    // composer is still on 'new') would hand listeners the wrong state.
    if (didSeed && get().currentContextId === contextId) {
      get().triggerChangeListeners('setSelectedModels', { modelIds: usableIds });
    }
    return didSeed || skipped;
  },

  // Pre-create a context in explicit Auto mode. Used for quick-create
  // auto-send conversations (_skipModelTransfer): one-shot generations whose
  // follow-ups should route through Auto, so default-model bootstrap and
  // model seeding must both stand down for this context.
  markContextExplicitAuto: (contextId) => {
    if (!contextId) return;
    set(state => {
      const existing = state.inputStates[contextId];
      if (existing?.modelSelectionTouched) return state;
      const base = existing || {
        text: '',
        files: [],
        selectedParameters: {},
        selectedButtons: [],
        selectedAgentIds: [],
        inputPlaceholder: "Message Gab AI",
        creditCalculation: {
          isCalculating: false,
          error: null,
          cost: null
        },
        banners: [],
        quote: '',
        dismissedBanners: [],
        editFileIds: [],
      };
      return {
        inputStates: {
          ...state.inputStates,
          [contextId]: {
            ...base,
            selectedModelIds: [],
            userExplicitAutoMode: true,
          }
        }
      };
    });
  },

  // Restore the model the user was last chatting with in this conversation
  // (e.g. after a page refresh wipes the in-memory input states). Only the
  // most recent user message counts, and Auto-mode sends are respected: the
  // backend stores the resolved model on the message even in Auto mode, so we
  // check the ___isAutoMode parameter flag to avoid pinning a model the user
  // never picked.
  // Returns true when settled; false when the caller should retry after the
  // referenced models are loaded into the model store.
  seedModelsFromConversationHistory: (conversationId) => {
    if (!conversationId) return true;
    const chat = getStores().useChatStore.getState().chats.find(c => c.id === conversationId);
    const messages = chat?.messages || [];

    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i];
      if (message?.role !== 'user') continue;
      // One-click "try with another model" sends are not composer preferences —
      // keep looking for an earlier user turn to seed from.
      if (message?.parameters?.___oneShotModel) continue;
      if (message?.parameters?.___isAutoMode) {
        // setContext may have already seeded a default text model into this
        // conversation's composer. Lock Auto so follow-ups stay Auto instead
        // of silently sending as that default (which would show the model card).
        get().markContextExplicitAuto(conversationId);
        return true;
      }
      // Prefer the models the user actually picked when intent-engine routing
      // replaced message.modelIds (e.g. political Arya → Gemini).
      const seedIds = (
        Array.isArray(message?.parameters?.___displayModelIds) &&
        message.parameters.___displayModelIds.length > 0
      ) ? message.parameters.___displayModelIds : message?.modelIds;
      if (Array.isArray(seedIds) && seedIds.length > 0) {
        return get().seedConversationModels(conversationId, seedIds);
      }
      return true;
    }
    return true;
  },

  setSelectedAgents: (agentIds, { botId } = {}) => {
    const { currentContextId, inputStates } = get();
    if (!currentContextId) return;
    const currentState = inputStates[currentContextId] || {};

    const extractIds = (input) => {
      if (!input) return [];
      if (Array.isArray(input)) {
        return input.map(item => (typeof item === 'object' && item?.id ? item.id : item));
      }
      return [typeof input === 'object' && input?.id ? input.id : input];
    };
    const uniqueAgentIds = [...new Set(extractIds(agentIds))];

    const prev = currentState.selectedAgentIds || [];
    const arraysEqual = prev.length === uniqueAgentIds.length && prev.every((v, i) => v === uniqueAgentIds[i]);
    const nextBotId = botId === undefined && arraysEqual ? currentState.botId : botId;
    if (arraysEqual && currentState.agentSelectionTouched && currentState.botId === nextBotId) {
      return;
    }

    const result = set(state => {
      const contextId = state.currentContextId;
      if (!contextId) return state;
      const curr = state.inputStates[contextId];

      return {
        inputStates: {
          ...state.inputStates,
          [contextId]: {
            ...curr,
            selectedAgentIds: uniqueAgentIds,
            agentSelectionTouched: true,
            botId: uniqueAgentIds.length === 1 && uniqueAgentIds[0] === nextBotId ? nextBotId : null,
          }
        }
      };
    });
    
    get().triggerChangeListeners('setSelectedAgents', { agentIds });
    return result;
  },

  // Restore explicit Bot threads after a reload without overriding a new selection.
  seedConversationAgent: (contextId, agentId) => {
    if (!contextId || !agentId) return;
    set(state => {
      const ctx = state.inputStates[contextId];
      if (!ctx || ctx.agentSelectionTouched || ctx.modelSelectionTouched) return state;
      return { inputStates: { ...state.inputStates, [contextId]: {
        ...ctx, selectedAgentIds: [agentId], agentSelectionTouched: true,
      } } };
    });
  },

  // Helper function to filter out invalid values
  filterValidIds: (ids) => {
    if (!Array.isArray(ids)) return [];
    return ids.filter(id => 
      id !== null && 
      id !== undefined && 
      id !== 'null' && 
      id !== 'undefined' && 
      id !== ''
    );
  },

  // Removed local credit cost computation; rely on API via calculateMessageCredits

  // Get formatted message for sending
  getFormattedMessage: (contextId) => {
    const state = get().getCurrentState(contextId);
    const files = state.files || [];

    // Handle both file objects and file IDs
    const fileIds = Array.isArray(files)
      ? files.map(file => (typeof file === 'object' ? file.id : file)).filter(Boolean)
      : [];

    // Split tools out of parameters into actions
    const selectedParameters = state.selectedParameters || {};
    const tools = selectedParameters.tools;
    const parametersWithoutTools = { ...selectedParameters };
    if (parametersWithoutTools.hasOwnProperty('tools')) {
      delete parametersWithoutTools.tools;
    }

    // Restructure parameters: separate global params from model-specific params
    // Model-specific params use composite keys like "modelId:paramName"
    const globalParameters = {};
    const modelParameters = {};

    Object.entries(parametersWithoutTools).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      
      // Check if this is a model-namespaced parameter (contains ":")
      const colonIndex = key.indexOf(':');
      if (colonIndex > 0) {
        // Model-specific parameter: "modelId:paramName"
        const modelId = key.substring(0, colonIndex);
        const paramName = key.substring(colonIndex + 1);
        
        if (!modelParameters[modelId]) {
          modelParameters[modelId] = {};
        }
        modelParameters[modelId][paramName] = value;
        } else {
        // Global parameter (not model-specific)
        globalParameters[key] = value;
        }
      });

    // Final parameters structure:
    // - Global params at top level
    // - Model-specific params nested under their modelId
    const finalParameters = {
      ...globalParameters,
      ...(Object.keys(modelParameters).length > 0 ? { modelParameters } : {}),
    };

    // Actions = existing selectedButtons + tools selections (array)
    const actions = Array.isArray(state.selectedButtons)
      ? [...state.selectedButtons]
      : [];
    if (Array.isArray(tools)) {
      tools.forEach(t => {
        if (t && !actions.includes(t)) actions.push(t);
      });
    } else if (typeof tools === 'string' && tools) {
      if (!actions.includes(tools)) actions.push(tools);
    }

    // Include editFileIds only if non-empty
    const editFileIds = Array.isArray(state.editFileIds) && state.editFileIds.length > 0
      ? state.editFileIds
      : undefined;

    // Extract fastMode from parameters (if set for new conversations)
    let fastMode = selectedParameters.fastMode || false;
    
    // For existing conversations, also check the chat's flags.fastMode
    // The contextId is the conversationId for existing chats
    const resolvedContextId = contextId || get().currentContextId;
    if (resolvedContextId && resolvedContextId !== 'new') {
      try {
        // Access the chat store to get the conversation's fast mode flag
        const chatStore = getStores().useChatStore.getState();
        const chat = chatStore.chats.find(c => c.id === resolvedContextId);
        if (chat?.flags?.fastMode) {
          fastMode = true;
        }
      } catch (e) {
        // Silently fail if chat store access fails
        console.warn('[getFormattedMessage] Could not access chat flags:', e);
      }
    }
    
    // Remove fastMode from finalParameters to avoid duplication
    if (finalParameters.hasOwnProperty('fastMode')) {
      delete finalParameters.fastMode;
    }

    const modelIds = get().filterValidIds(state.selectedModelIds);
    // Collection defaults (and any other pre-selected model) are an explicit
    // pick, not Auto. Stamp them so political/intent routing can swap the
    // inference model without changing the Arya (or other) card.
    const isExplicitAuto = state.userExplicitAutoMode === true || modelIds.length === 0;
    const hasDisplayIds = Array.isArray(finalParameters.___displayModelIds)
      && finalParameters.___displayModelIds.length > 0;
    if (!isExplicitAuto && !hasDisplayIds) {
      finalParameters.___displayModelIds = modelIds;
    }

    return {
      text: state.text,
      parameters: finalParameters,
      actions,
      fileIds,
      quote: state.quote,
      modelIds,
      agentIds: get().filterValidIds(state.selectedAgentIds),
      ...(state.botId && state.selectedAgentIds?.length === 1 && state.selectedAgentIds[0] === state.botId
        ? { botId: state.botId } : {}),
      ...(editFileIds && { editFileIds }),
      ...(fastMode && { fastMode: true }),
    };
  },

  // Clear input state for current context
  clearInput: (deep = false) => {
    const result = set(state => {
      const contextId = state.currentContextId;

      if (contextId === 'new') deep = true;

      if (!contextId) return state;

      const ctx = state.inputStates[contextId];
      if (!ctx) return state;

      const defaultModelIds = (deep && contextId === 'new') ? get()._getDefaultModelIds() : [];
      const keptModelIds = deep ? defaultModelIds : ctx.selectedModelIds;
      const keptSelectedParameters = deep ? get()._getDefaultSelectedParameters() : ctx.selectedParameters;
      const currentAgentIds = ctx.selectedAgentIds;
      const keptAgentIds = deep ? [] : (Array.isArray(currentAgentIds) && currentAgentIds.length > 0 ? [...currentAgentIds] : (currentAgentIds ?? []));

      return {
        inputStates: {
          ...state.inputStates,
          [contextId]: {
            text: '',
            files: [],
            selectedParameters: keptSelectedParameters,
            selectedButtons: deep ? [] : ctx.selectedButtons,
            selectedModelIds: keptModelIds,
            selectedAgentIds: keptAgentIds,
            agentSelectionTouched: deep ? false : ctx.agentSelectionTouched,
            botId: deep ? null : ctx.botId,
            userExplicitAutoMode: deep ? false : (ctx.userExplicitAutoMode ?? false),
            modelSelectionTouched: deep ? false : (ctx.modelSelectionTouched ?? false),
            inputPlaceholder: deep ? "Message Gab AI" : (ctx.inputPlaceholder || "Message Gab AI"),
            creditCalculation: {
              isCalculating: false,
              error: null,
              cost: null,
            },
            banners: deep ? [] : ctx.banners, // Clear banners if deep clean
            quote: '',
            dismissedBanners: deep ? [] : (ctx.dismissedBanners || []),
            editFileIds: [], // Always clear edit file IDs on input clear
          }
        }
      };
    });
    
    get().triggerChangeListeners('clearInput', { deep });
    const contextId = get().currentContextId;
    if (contextId) clearComposerDraft(contextId);
    return result;
  },

  // Clear all states (useful for logout, etc.)
  clearAll: () => {
    const result = set({
      currentContextId: null,
      inputStates: {}
    });
    
    get().triggerChangeListeners('clearAll', {});
    clearAllComposerDrafts();
    return result;
  },

  // Request focus on a ChatInput by contextId. Components listen to `focusRequest` and focus their TextInput when the contextId matches and tick changes.
  requestInputFocus: (contextId) => {
    set(state => ({
      focusRequest: {
        contextId: contextId ?? null,
        tick: (state.focusRequest?.tick || 0) + 1,
      },
    }));
  },

  // Initialize a new chat with files - proper implementation
  initializeNewChatWithFiles: (fileIds, prompt = '') => {
    if (!fileIds) return;
    if (!Array.isArray(fileIds)) fileIds = [fileIds];

    const contextId = 'new';
    const fileStore = getStores().useFileStore.getState();
    const foundFiles = fileIds.map(id => fileStore.files[id]).filter(Boolean);

    const result = set(state => ({
      currentContextId: contextId,
      inputStates: {
        ...state.inputStates,
        [contextId]: {
          text: prompt || '',
          files: foundFiles,
          selectedParameters: {},
          selectedButtons: [],
          selectedModelIds: [],
          selectedAgentIds: [],
          inputPlaceholder: "Message Gab AI",
          creditCalculation: {
            isCalculating: false,
            error: null,
            cost: null
          },
          banners: [], // Initialize empty banners array
          quote: '',
          dismissedBanners: [],
        }
      }
    }));
    
    get().triggerChangeListeners('initializeNewChatWithFiles', { fileIds, foundFiles, prompt });
    return result;
  },

  // Initialize a new chat with image generation prompt
  // If autoSend is true, the message will be automatically sent when the chat page loads
  // `files` is an optional array of file objects (must include an `id` from a
  // user-owned File record) to pre-attach to the chat input. When `autoSend`
  // is true, their ids are passed through pendingAutoSend so the auto-send
  // path includes them as fileIds (e.g. image-to-image remix source image).
  initializeImageGeneration: (prompt, modelId = null, autoSend = false, files = []) => {
    const contextId = 'new';
    const safeFiles = Array.isArray(files) ? files.filter(Boolean) : [];
    const fileIds = safeFiles
      .map(f => (typeof f === 'object' ? f.id : f))
      .filter(Boolean);

    const result = set(state => ({
      currentContextId: contextId,
      pendingAutoSend: autoSend && prompt ? {
        text: prompt,
        action: 'create-image',
        modelId: modelId,
        fileIds,
      } : null,
      inputStates: {
        ...state.inputStates,
        [contextId]: {
          text: prompt || '',
          files: safeFiles,
          selectedParameters: { action: 'create-image' },
          selectedButtons: [],
          selectedModelIds: modelId ? [modelId] : [],
          selectedAgentIds: [],
          inputPlaceholder: "Describe an image",
          creditCalculation: {
            isCalculating: false,
            error: null,
            cost: null
          },
          banners: [],
          quote: '',
          dismissedBanners: [],
        }
      }
    }));

    get().triggerChangeListeners('initializeImageGeneration', { prompt, modelId, autoSend, fileIds });
    return result;
  },

  // Initialize a new chat with video generation prompt
  // If autoSend is true, the message will be automatically sent when the chat page loads
  // `files` is an optional array of file objects (must include an `id` from a
  // user-owned File record) to pre-attach to the chat input. When `autoSend`
  // is true, their ids are passed through pendingAutoSend so the auto-send
  // path includes them as fileIds (e.g. image-to-video source image).
  initializeVideoGeneration: (prompt, modelId = null, autoSend = false, files = []) => {
    const contextId = 'new';
    const safeFiles = Array.isArray(files) ? files.filter(Boolean) : [];
    const fileIds = safeFiles
      .map(f => (typeof f === 'object' ? f.id : f))
      .filter(Boolean);

    const result = set(state => ({
      currentContextId: contextId,
      pendingAutoSend: autoSend && prompt ? {
        text: prompt,
        action: 'create-video',
        modelId: modelId,
        fileIds,
      } : null,
      inputStates: {
        ...state.inputStates,
        [contextId]: {
          text: prompt || '',
          files: safeFiles,
          selectedParameters: { action: 'create-video' },
          selectedButtons: [],
          selectedModelIds: modelId ? [modelId] : [],
          selectedAgentIds: [],
          inputPlaceholder: "Describe a video",
          creditCalculation: {
            isCalculating: false,
            error: null,
            cost: null
          },
          banners: [],
          quote: '',
          dismissedBanners: [],
        }
      }
    }));

    get().triggerChangeListeners('initializeVideoGeneration', { prompt, modelId, autoSend, fileIds });
    return result;
  },

  // Initialize a new chat with music generation prompt
  // If autoSend is true, the message will be automatically sent when the chat page loads
  // Initialize music generation with lyrics (prompt) and optional description
  // description: The style/mood description for the music model
  // prompt: The lyrics to be sung
  initializeMusicGeneration: (prompt, modelId = null, autoSend = false, description = null) => {
    const contextId = 'new';

    // Music can be instrumental — autoSend should fire when we have either a
    // description (style) or lyrics (prompt). Without this, leaving the lyrics
    // box blank silently swallows the submit on the explore floating input.
    const pendingAutoSendValue = autoSend && (prompt || description) ? {
      text: prompt || '',
      action: 'create-music',
      modelId: modelId,
      description: description, // Pass description for music generation
    } : null;

    // Build selectedParameters with model-namespaced description if modelId is provided
    // This uses the composite key pattern "modelId:paramName" that getFormattedMessage expects
    const selectedParameters = { action: 'create-music' };
    if (description && modelId) {
      selectedParameters[`${modelId}:description`] = description;
    }

    const result = set(state => ({
      currentContextId: contextId,
      pendingAutoSend: pendingAutoSendValue,
      inputStates: {
        ...state.inputStates,
        [contextId]: {
          text: prompt || '',
          files: [],
          selectedParameters,
          selectedButtons: [],
          selectedModelIds: modelId ? [modelId] : [],
          selectedAgentIds: [],
          inputPlaceholder: "Enter lyrics",
          creditCalculation: {
            isCalculating: false,
            error: null,
            cost: null
          },
          banners: [],
          quote: '',
          dismissedBanners: [],
        }
      }
    }));
    
    get().triggerChangeListeners('initializeMusicGeneration', { prompt, modelId, autoSend, description });
    return result;
  },

  // Initialize a new chat with 3D-model generation pre-configured.
  // - prompt: text-to-3d description (or image-to-3d caption)
  // - modelId: optional 3D model id; backend resolves a default if absent
  // - imageToThreeD: when true, marks the action as 'image-to-3d' and accepts
  //   one or more attached files (the source image)
  // - files: source images for image-to-3d (ignored for text-to-3d)
  initialize3DGeneration: (prompt, modelId = null, autoSend = false, imageToThreeD = false, files = []) => {
    const contextId = 'new';
    const action = imageToThreeD ? 'image-to-3d' : 'create-3d';

    const fileIds = (files || [])
      .map(f => f?.id || f?._id)
      .filter(Boolean);

    const pendingAutoSendValue = autoSend && (prompt || fileIds.length) ? {
      text: prompt || '',
      action,
      modelId: modelId,
      ...(fileIds.length ? { fileIds } : {}),
    } : null;

    const result = set(state => ({
      currentContextId: contextId,
      pendingAutoSend: pendingAutoSendValue,
      inputStates: {
        ...state.inputStates,
        [contextId]: {
          text: prompt || '',
          files: files || [],
          selectedParameters: { action },
          selectedButtons: [],
          selectedModelIds: modelId ? [modelId] : [],
          selectedAgentIds: [],
          inputPlaceholder: imageToThreeD
            ? 'Describe the 3D model (optional)'
            : 'Describe a 3D model',
          creditCalculation: {
            isCalculating: false,
            error: null,
            cost: null
          },
          banners: [],
          quote: '',
          dismissedBanners: [],
        }
      }
    }));

    get().triggerChangeListeners('initialize3DGeneration', { prompt, modelId, autoSend, imageToThreeD, fileIds });
    return result;
  },

  // Initialize a new chat with web search enabled
  // If autoSend is true, the message will be automatically sent when the chat page loads
  initializeWebSearch: (prompt, autoSend = false) => {
    const contextId = 'new';

    const result = set(state => ({
      currentContextId: contextId,
      pendingAutoSend: autoSend && prompt ? {
        text: prompt,
        action: 'search-web',
      } : null,
      inputStates: {
        ...state.inputStates,
        [contextId]: {
          text: prompt || '',
          files: [],
          selectedParameters: { tools: ['search-web'] },
          selectedButtons: [],
          selectedModelIds: [],
          selectedAgentIds: [],
          inputPlaceholder: "Search the web...",
          creditCalculation: {
            isCalculating: false,
            error: null,
            cost: null
          },
          banners: [],
          quote: '',
          dismissedBanners: [],
        }
      }
    }));
    
    get().triggerChangeListeners('initializeWebSearch', { prompt, autoSend });
    return result;
  },

  // Clear the pending auto-send message
  clearPendingAutoSend: () => {
    set({ pendingAutoSend: null });
  },

  // Set highlighted missing parameters (with auto-clear after timeout)
  setHighlightedMissingParameters: (parameterIds) => {
    set({ highlightedMissingParameterIds: parameterIds });
    // Auto-clear after 3 seconds
    setTimeout(() => {
      set({ highlightedMissingParameterIds: [] });
    }, 3000);
  },

  // Clear highlighted missing parameters
  clearHighlightedMissingParameters: () => {
    set({ highlightedMissingParameterIds: [] });
  },

  // Add banner management functions
  addBanner: (bannerType, bannerProps = {}) => {
    const result = set(state => {
      const contextId = state.currentContextId;
      if (!contextId) return state;
      const currentState = state.inputStates[contextId];

      // Skip if this banner type was dismissed for this context
      if (currentState.dismissedBanners?.includes(bannerType)) {
        return state;
      }

      // Don't add if banner of this type already exists
      if (currentState.banners.some(banner => banner.type === bannerType)) {
        return state;
      }

      return {
        inputStates: {
          ...state.inputStates,
          [contextId]: {
            ...currentState,
            banners: [...currentState.banners, { type: bannerType, props: bannerProps }]
          }
        }
      };
    });

    get().triggerChangeListeners('addBanner', { bannerType, bannerProps });
    return result;
  },

  removeBanner: (bannerType) => {
    const result = set(state => {
      const contextId = state.currentContextId;
      if (!contextId) return state;
      const currentState = state.inputStates[contextId];

      // Skip if no banners of this type exist — avoids creating new state
      // and triggering unnecessary re-renders during initial mount
      if (!currentState.banners.some(banner => banner.type === bannerType)) {
        return state;
      }

      return {
        inputStates: {
          ...state.inputStates,
          [contextId]: {
            ...currentState,
            banners: currentState.banners.filter(banner => banner.type !== bannerType)
          }
        }
      };
    });

    get().triggerChangeListeners('removeBanner', { bannerType });
    return result;
  },

  updateBanner: (bannerType, bannerProps = {}) => {
    const result = set(state => {
      const contextId = state.currentContextId;
      if (!contextId) return state;
      const currentState = state.inputStates[contextId];

      // Skip if the banner doesn't exist — avoids creating new state
      if (!currentState.banners.some(banner => banner.type === bannerType)) {
        return state;
      }

      return {
        inputStates: {
          ...state.inputStates,
          [contextId]: {
            ...currentState,
            banners: currentState.banners.map(banner => 
              banner.type === bannerType 
                ? { ...banner, props: { ...banner.props, ...bannerProps } }
                : banner
            )
          }
        }
      };
    });

    get().triggerChangeListeners('updateBanner', { bannerType, bannerProps });
    return result;
  },

  // Permanently dismiss a banner for the current context (until deep clear)
  dismissBanner: (bannerType) => {
    const result = set(state => {
      const contextId = state.currentContextId;
      if (!contextId) return state;
      const currentState = state.inputStates[contextId];

      const alreadyDismissed = (currentState.dismissedBanners || []).includes(bannerType);

      return {
        inputStates: {
          ...state.inputStates,
          [contextId]: {
            ...currentState,
            dismissedBanners: alreadyDismissed 
              ? currentState.dismissedBanners 
              : [...(currentState.dismissedBanners || []), bannerType],
            banners: currentState.banners.filter(banner => banner.type !== bannerType)
          }
        }
      };
    });

    get().triggerChangeListeners('dismissBanner', { bannerType });
    return result;
  },

  // ===== CHANGE LISTENER SYSTEM =====
  
  // Add a change listener that gets called whenever state changes
  addChangeListener: (listener) => {
    if (typeof listener !== 'function') {
      console.warn('Change listener must be a function');
      return () => {}; // Return no-op unsubscribe
    }

    set(state => ({
      changeListeners: [...state.changeListeners, listener]
    }));

    // Return unsubscribe function
    return () => {
      set(state => ({
        changeListeners: state.changeListeners.filter(l => l !== listener)
      }));
    };
  },

  // Remove a specific change listener
  removeChangeListener: (listener) => {
    set(state => ({
      changeListeners: state.changeListeners.filter(l => l !== listener)
    }));
  },

  // Clear all change listeners
  clearChangeListeners: () => {
    set(state => ({
      changeListeners: []
    }));
  },

  // Trigger all change listeners with current state
  triggerChangeListeners: (changeType, changeData = {}) => {
    const state = get();
    const currentState = state.getCurrentState();
    const contextId = state.currentContextId;
    
    // Call all registered listeners
    state.changeListeners.forEach(listener => {
      try {
        listener({
          type: changeType,
          data: changeData,
          currentState,
          contextId,
          timestamp: Date.now()
        });
      } catch (error) {
        console.error('Error in ChatInput change listener:', error);
      }
    });
  },

  // Return true if any required parameter is missing or invalid for the current context
  isMissingRequiredParameters: () => {
    const state = get().getCurrentState();
    const params = Array.isArray(state?.parameters) ? state.parameters : [];
    const selected = state?.selectedParameters || {};

    const isEmpty = (val) => {
      if (val === null || val === undefined) return true;
      if (typeof val === 'string') return val.trim().length === 0;
      if (Array.isArray(val)) return val.length === 0 || val.every(v => v === null || v === undefined || v === '');
      if (typeof val === 'object') return Object.keys(val).length === 0;
      return false;
    };

    const isInvalidByType = (param, val) => {
      const validation = param?.validation || {};
      const comp = param?.ui?.component;

      // Options validation: ensure value(s) exist in provided options list
      const opts = validation?.options;
      if (Array.isArray(opts) && opts.length > 0) {
        const allowed = new Set(opts.map(o => o?.value));
        if (comp === 'multiselect') {
          const arr = Array.isArray(val) ? val : (val !== undefined && val !== null && val !== '' ? [val] : []);
          if (arr.length > 0 && !arr.every(item => allowed.has(item))) return true;
        } else if (comp === 'select' || comp === 'action') {
          if (val !== undefined && val !== null && val !== '' && !allowed.has(val)) {
            return true;
          } else {
            return false;
          }
        }
      }
      
      // Daterange: require both start and end
      if (comp === 'daterange') {
        if (!Array.isArray(val) || val.length !== 2) return true;
        const [s, e] = val;
        if (!s || !e) return true;
        return false;
      }

      // Textarea-multi: require at least one non-empty field
      if (comp === 'textarea-multi') {
        if (typeof val !== 'object' || val === null) return true;
        const anyFilled = Object.values(val || {}).some(v => typeof v === 'string' && v.trim().length > 0);
        return !anyFilled;
      }

      // Multiselect: require at least one selection
      if (comp === 'multiselect') {
        const arr = Array.isArray(val) ? val : (val ? [val] : []);
        return arr.length === 0;
      }

      // String min/max length (and non-empty)
      if (typeof val === 'string') {
        const minLen = validation?.string?.minLength;
        const maxLen = validation?.string?.maxLength;
        const len = val.trim().length;
        if (typeof minLen === 'number' && len < minLen) return true;
        if (typeof maxLen === 'number' && len > maxLen) return true;
        return len === 0;
      }

      // Number min/max
      if (typeof val === 'number') {
        const min = validation?.number?.min;
        const max = validation?.number?.max;
        if (typeof min === 'number' && val < min) return true;
        if (typeof max === 'number' && val > max) return true;
        return false;
      }

      // Default: not empty
      return isEmpty(val);
    };

    // File-typed params (image / audio / video / document upload components)
    // store their data in `inputState.files`, NOT on `param.value`. They are
    // gated separately in `ChatInput.jsx` via `unsatisfiedRequiredFileParams`
    // (which matches required file params against attached files by fileType).
    // If we asked `isInvalidByType` about them here it would always return
    // true — `param.value` is `null` even after the user uploads via the
    // ghost card — and produce a bogus "Please fill in all required
    // parameters: Audio" toast on send for models like Wizper.
    const FILE_COMPONENTS = new Set([
      'file', 'fileMultiple',
      'image', 'imageMultiple', 'multiImageUpload', 'imageMask',
      'audio', 'audioWaveform',
      'video', 'videoTrimmer',
      'pointCloud', 'layerViewer',
    ]);

    const missingParameters = [];
    for (const p of params) {
      if (!p || !p.validation || !p.validation.required) continue;
      if (p.type === 'file' || FILE_COMPONENTS.has(p?.ui?.component)) continue;
      const key = p.id || p.name;
      const value = (selected[key] !== undefined) ? selected[key] : (p.value ?? null);
      if (isInvalidByType(p, value)) {
        missingParameters.push(p);
      }
    }

    return {
      isMissing: missingParameters.length > 0,
      missingParameters,
    };
  },
}));

export { useChatInputStore };
export default useChatInputStore;
